/**
 * Cohort Coaches API Service
 * Manages coaches within a cohort's potential and assigned pools
 */

import apiClient from './client';
import { buildQueryString, type BaseListParams } from './utils';
import type { PaginatedResponse } from '../../types';

// Pool status for coaches in a cohort
export type CohortCoachPoolStatus = 'potential' | 'assigned';

// Availability status for coach responses
export type CohortCoachAvailabilityStatus =
  | 'not_requested'
  | 'pending_response'
  | 'available'
  | 'partially_available'
  | 'unavailable';

// Contract status for coach assignment workflow (EC-36)
export type CohortCoachContractStatus = 'pending' | 'announced' | 'signed';

// Cohort coach with details for display
export interface CohortCoachWithDetails {
  id: string;
  cohort_id: string;
  user_id: string;
  assigned_at: string;
  removed_at: string | null;
  pay_rate_cents: number | null;
  pay_rate_type: string | null;
  is_active: boolean;
  pool_status: CohortCoachPoolStatus;
  availability_status: CohortCoachAvailabilityStatus;
  availability_percent: number;
  confirmed_modalities: string[];
  confirmed_time_slots: string[];
  slot_availability: SlotAvailabilityMap | null; // Per-slot availability status
  max_groups: number | null; // Max groups coach is willing to take on
  is_flexible_groups: boolean; // Coach is willing to take as many groups as needed
  // Contract status fields (EC-36)
  contract_status: CohortCoachContractStatus;
  announced_at: string | null;
  contract_signed_at: string | null;
  created_at: string;
  updated_at: string;

  // User fields
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;

  // Coach profile fields
  coach_profile_id: string;
  coach_type: 'medley' | 'partner';
  partner_name: string | null;
  status: string;
  location: string | null;
  home_base_timezone: string | null;
  expertise_areas: string[];
  coaching_specializations: string | null;
  modality_proficiencies: string[];
  modality_approvals: string[];

  // Computed fields
  group_count: number;
  assigned_groups: Array<{ id: string; name: string }>;
  one_on_one_count: number;
  workshop_session_count: number;
  satisfaction_score: number | null;
  satisfaction_survey_count: number;
}

// Stats for the Coaches tab stat cards
export interface CohortCoachStats {
  coaches_in_cohort: number;
  from_pool: number;
  fully_assigned: number;
  all_modalities_covered: number;
  needs_assignment: number;
  groups_needing_coach: number;
  participants_needing_coach: number;
  upcoming_sessions: number;
  next_session_weeks: number | null;
  // Contract status stats (EC-36)
  contracts_signed: number;
  announcements_sent: number;
  contracts_awaiting: number;
}

// Coach available for adding to cohort
export interface AvailableCoachForPool {
  user_id: string;
  /** The Medley email (users.email). Null for a coach without one. */
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  coach_profile_id: string;
  /** MED-1355: the personal (non-Medley) email. Staff only. */
  preferred_email?: string | null;
  coach_type: 'medley' | 'partner';
  partner_name: string | null;
  // MED-683: derived 'active' | 'inactive'
  status: string;
  manually_inactive: boolean;
  modality_approvals: string[];
  location: string | null;
  home_base_timezone: string | null;
  expertise_areas: string[];
  coaching_specializations: string | null;
  active_cohort_count: number;
  satisfaction_score: number | null;
  satisfaction_survey_count: number;
  already_in_cohort: boolean;
}

// List params for cohort coaches
export interface CohortCoachListParams extends BaseListParams {
  pool_status?: CohortCoachPoolStatus;
  availability_status?: CohortCoachAvailabilityStatus;
}

// List params for available pool
export interface AvailablePoolListParams extends BaseListParams {
  status?: string | string[];
  region?: string;
  timezone?: string;
  program_id?: string;
  coach_type?: 'medley' | 'partner';
}

// Request to add coaches to pool
export interface AddCoachesToPoolRequest {
  coach_user_ids: string[];
}

// Request to send availability request
export interface SendAvailabilityRequestPayload {
  coach_user_ids: string[];
  /**
   * MDF-233: optional, because a reminder-only send does not restate the ask.
   * Omit both when every selected coach already holds an open request and the
   * CM did not choose to replace it.
   */
  modalities?: string[];
  time_slot_ids?: string[];
  response_deadline?: string;
  cohort_context?: string;
  email_subject?: string;
  email_message?: string;
  /**
   * MDF-233: what to do for a coach who already holds an open request in this
   * cohort. 'remind' (the default) reuses that request and its magic link and
   * changes nothing. 'replace' supersedes it and creates a new request. The
   * API never replaces unless this says so.
   */
  on_existing?: 'remind' | 'replace';
}

/** MDF-233: what a send actually did for one coach. */
export type AvailabilitySendAction = 'requested' | 'reminded' | 'resent';

// Response from send availability request
export interface SendAvailabilityRequestResponse {
  success: boolean;
  requests_sent: number;
  coaches_emailed: string[];
  // MDF-233: per-action counts, so the modal can report "2 new requests,
  // 3 reminders" instead of a single misleading number.
  requested_count?: number;
  reminded_count?: number;
  resent_count?: number;
  actions?: Array<{ coach_user_id: string; action: AvailabilitySendAction }>;
  dropped_user_ids?: string[];
  dropped_count?: number;
}

/**
 * MDF-233: one coach's open request, as returned by the preview endpoint.
 * The send modal uses this to name who gets a reminder and to prefill the
 * form, so that changing nothing sends a reminder.
 */
export interface AvailabilitySendPreviewRow {
  coach_user_id: string;
  request_id: string;
  requested_at: string;
  response_deadline: string | null;
  modalities: string[];
  requested_time_slots: string[];
  reminder_count: number;
  last_reminded_at: string | null;
}

export interface AvailabilitySendPreview {
  open_requests: AvailabilitySendPreviewRow[];
  coaches_without_open_request: string[];
  would_remind: number;
  would_request: number;
}

// ============================================================================
// Coach Detail Modal Types (MED-213)
// ============================================================================

// Interest status for coach response
export type InterestStatus = 'interested' | 'not_interested';

// Availability request status
export type AvailabilityRequestStatus = 'pending' | 'confirmed' | 'declined' | 'expired';

// Slot availability status
export type SlotAvailabilityStatus = 'full' | 'partial' | 'unavailable';

// Conflict date entry for partial availability
export interface SlotConflict {
  date: string;
  reason?: string;
}

// Per-slot availability data
export interface SlotAvailability {
  status: SlotAvailabilityStatus;
  conflicts?: SlotConflict[];
}

// Full slot availability object (keyed by slot ID)
export type SlotAvailabilityMap = Record<string, SlotAvailability>;

// Time slot for coach display
export interface TimeSlotForCoach {
  id: string;
  day_of_week: string;
  time: string;
  timezone: string;
  slot_number: number;
}

// Group assignment for current assignments section
export interface CoachGroupAssignment {
  id: string;
  name: string;
  participant_count: number;
}

// Session counts by type for this cohort
export interface CoachSessionCounts {
  group: number;
  one_on_one: number;
  workshop: number;
}

// Availability response data for modal display
export interface CoachAvailabilityResponseForModal {
  interest_status: InterestStatus | null;
  status: AvailabilityRequestStatus;
  responded_at: string | null;
  requested_at: string;
  slot_availability: SlotAvailabilityMap | null;
  max_groups: number | null;
  is_flexible_groups: boolean;
  additional_notes: string | null;
  time_slots: TimeSlotForCoach[];
  session_count: number;
  // MED-1026: group-coaching session duration (minutes) used to render each
  // time-slot window in the CM availability modal.
  session_duration_minutes: number;
  // Attribution fields (CM-entered availability)
  entered_by_user_id: string | null;
  entered_by_name: string | null;
  entered_at: string | null;
  // Request metadata for edit form
  modalities: string[];
  compensation_text: string | null;
  response_deadline: string | null;
  // Cohort context for Request Details section
  partner_name: string | null;
  program_name: string | null;
  cohort_start_date: string | null;
  cohort_end_date: string | null;
}

// Payload for CM-submitted availability response
export interface AdminAvailabilityResponsePayload {
  interest_status: InterestStatus;
  slot_availability?: SlotAvailabilityMap;
  max_groups?: number;
  is_flexible_groups?: boolean;
}

// Full coach detail for modal (Profile + Availability tabs)
export interface CoachDetailForModal {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  coach_profile_id: string;
  coach_type: 'medley' | 'partner';
  status: string;
  coaching_specializations: string | null;
  location: string | null;
  home_base_timezone: string | null;
  timezone_preference: string | null;
  modality_proficiencies: string[];
  modality_approvals: string[];
  internal_notes: string | null;
  cohort_coach_id: string;
  pool_status: CohortCoachPoolStatus;
  availability_status: CohortCoachAvailabilityStatus;
  availability_percent: number;
  // Contract status (EC-36)
  contract_status: CohortCoachContractStatus;
  announced_at: string | null;
  contract_signed_at: string | null;
  satisfaction_rating: number | null;
  total_reviews: number;
  session_counts: CoachSessionCounts;
  group_assignments: CoachGroupAssignment[];
  availability_response: CoachAvailabilityResponseForModal | null;
}

// Past cohort entry for Past Cohorts tab
export interface CoachPastCohort {
  cohort_id: string;
  cohort_name: string;
  partner_name: string | null;
  start_date: string | null;
  end_date: string | null;
  group_count: number;
  satisfaction_rating: number | null;
  total_sessions: number;
}

// ============================================================================
// Coach Announcement & Contract Types (MED-232 / EC-34, EC-35, EC-36)
// ============================================================================

// Coach info for announcement modal recipient list
export interface CoachAnnouncementRecipient {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  contract_status: CohortCoachContractStatus;
  announced_at: string | null;
  assigned_groups: Array<{ id: string; name: string }>;
  group_count: number;
}

// Response from sending announcements
export interface SendAnnouncementsResponse {
  success: boolean;
  announcements_sent: number;
  coaches_emailed: string[];
}

// MED-761: Merge tag definition for coach announcements
export interface CoachMergeTagDefinition {
  tag: string;
  label: string;
  description: string;
}

// MED-761: Response from announcement preview
export interface CoachAnnouncementPreviewResponse {
  subject: string;
  body: string;
  unresolvableTags: string[];
  sampleCoach: { first_name: string; email: string };
  mergeTagDefinitions: CoachMergeTagDefinition[];
}

// MED-761: Response from sending test announcement
export interface SendTestAnnouncementResponse {
  email: string;
}

// Callout data for Groups view (EC-34, EC-36)
export interface CoachCalloutData {
  callout_type: 'announcement' | 'contract' | null;
  all_groups_matched: boolean;
  total_groups: number;
  total_sessions: number;
  coaches_needing_announcement: number;
  coaches_awaiting_contract: number;
  coaches_awaiting_names: string[];
}

// ============================================================================
// Coach Schedule Grid Types (MED-333)
// ============================================================================

export type ScheduleCellState =
  | 'scheduled'
  | 'available'
  | 'available_conditions'
  | 'unavailable'
  | 'awaiting_response'
  | 'not_requested';

// Per-time-slot cell in expanded row (MED-454)
export interface ScheduleGridSlotCell {
  time_slot_id: string;
  state: ScheduleCellState;
  group_name?: string;
  group_id?: string;
  session_id?: string;
  conflict_date?: string;
  conflict_cohort_name?: string;
}

// Time slot metadata for expanded row labels (MED-454)
export interface ScheduleGridTimeSlot {
  id: string;
  identifier: string;
  slot_number: number;
  day_of_week: string;
  display_time: string;
  timezone_abbreviation: string;
}

// Uncovered session slot info (MED-454)
export interface UncoveredSessionSlot {
  session_number: number;
  session_date: string | null;
}

// Uncovered time slot with affected sessions (MED-454)
export interface UncoveredTimeSlotInfo {
  time_slot: ScheduleGridTimeSlot;
  sessions: UncoveredSessionSlot[];
}

// Cross-cohort session for "Show Other Cohorts" (MED-454)
export interface CrossCohortSession {
  cohort_id: string;
  cohort_name: string;
  time_slot_id: string;
  scheduled_date: string;
}

export interface ScheduleGridCell {
  state: ScheduleCellState;
  group_name?: string;
  group_names?: string[];
  group_id?: string;
  session_id?: string;
  remaining_capacity?: number;
  max_capacity?: number;
  conditions?: string;
  // MED-454: Per-slot availability summary
  available_slots?: number;
  total_slots?: number;
  conflict_slots?: number;
  slot_details?: ScheduleGridSlotCell[];
}

export interface ScheduleGridSession {
  session_number: number;
  scheduled_date: string | null;
  scheduled_time: string | null;
  timezone: string | null;
  topic_name: string | null;
}

export interface ScheduleGridCoach {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
  group_count: number;
  max_groups: number | null;
  is_flexible_groups: boolean;
  confirmed_modalities: string[];
  modality_proficiencies: string[];
  availability_status: CohortCoachAvailabilityStatus;
  cells: Record<number, ScheduleGridCell>;
  cross_cohort_sessions?: CrossCohortSession[]; // MED-454
}

export interface CoachScheduleGridResponse {
  sessions: ScheduleGridSession[];
  coaches: ScheduleGridCoach[];
  summary: {
    total_coaches: number;
    total_sessions: number;
  };
  time_slots: ScheduleGridTimeSlot[]; // MED-454
  uncovered: UncoveredTimeSlotInfo[]; // MED-454
}

export const cohortCoachesApi = {
  /**
   * List coaches in a cohort
   */
  list: async (
    cohortId: string,
    params?: CohortCoachListParams,
  ): Promise<PaginatedResponse<CohortCoachWithDetails>> => {
    const url = `/api/cohorts/${cohortId}/coaches${buildQueryString(params)}`;
    return apiClient.getPaginated<CohortCoachWithDetails>(url);
  },

  /**
   * Get coach statistics for a cohort
   */
  getStats: async (cohortId: string): Promise<CohortCoachStats> => {
    return apiClient.get<CohortCoachStats>(`/api/cohorts/${cohortId}/coaches/stats`);
  },

  /**
   * Get coaches available to add to the cohort
   */
  getAvailablePool: async (
    cohortId: string,
    params?: AvailablePoolListParams,
  ): Promise<PaginatedResponse<AvailableCoachForPool>> => {
    const url = `/api/cohorts/${cohortId}/coaches/available-pool${buildQueryString(params)}`;
    return apiClient.getPaginated<AvailableCoachForPool>(url);
  },

  /**
   * Add coaches to the potential pool
   */
  addToPool: async (
    cohortId: string,
    coachUserIds: string[],
  ): Promise<CohortCoachWithDetails[]> => {
    const data: AddCoachesToPoolRequest = { coach_user_ids: coachUserIds };
    return apiClient.post<CohortCoachWithDetails[]>(`/api/cohorts/${cohortId}/coaches`, data);
  },

  /**
   * Remove a coach from the cohort
   * @param sendThankYou - If true, sends a thank you email to the coach (EC-35)
   */
  remove: async (cohortId: string, coachUserId: string, sendThankYou?: boolean): Promise<void> => {
    const params = sendThankYou ? '?sendThankYou=true' : '';
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/coaches/${coachUserId}${params}`);
  },

  /**
   * MDF-233: ask the API what a send would do, before doing it.
   *
   * Splits the selection into coaches who already hold an open request in this
   * cohort and coaches who do not. Read only: nothing is written and no email
   * is sent.
   */
  previewAvailabilitySend: async (
    cohortId: string,
    coachUserIds: string[],
  ): Promise<AvailabilitySendPreview> => {
    return apiClient.post<AvailabilitySendPreview>(
      `/api/cohorts/${cohortId}/coaches/availability-request/preview`,
      { coach_user_ids: coachUserIds },
    );
  },

  /**
   * Send availability request to selected coaches
   */
  sendAvailabilityRequest: async (
    cohortId: string,
    payload: SendAvailabilityRequestPayload,
  ): Promise<SendAvailabilityRequestResponse> => {
    return apiClient.post<SendAvailabilityRequestResponse>(
      `/api/cohorts/${cohortId}/coaches/availability-request`,
      payload,
    );
  },

  /**
   * Send test availability request email
   */
  sendTestEmail: async (
    cohortId: string,
    emailSubject?: string,
    emailMessage?: string,
    responseDeadline?: string,
  ): Promise<{ success: boolean; message: string }> => {
    return apiClient.post<{ success: boolean; message: string }>(
      `/api/cohorts/${cohortId}/coaches/availability-request/test-email`,
      {
        email_subject: emailSubject,
        email_message: emailMessage,
        response_deadline: responseDeadline,
      },
    );
  },

  /**
   * Get coach detail for modal (MED-213)
   */
  getCoachDetail: async (
    cohortId: string,
    coachUserId: string,
  ): Promise<CoachDetailForModal> => {
    return apiClient.get<CoachDetailForModal>(
      `/api/cohorts/${cohortId}/coaches/${coachUserId}/detail`,
    );
  },

  /**
   * Get coach's past cohorts (MED-213)
   */
  getCoachPastCohorts: async (
    cohortId: string,
    coachUserId: string,
  ): Promise<CoachPastCohort[]> => {
    return apiClient.get<CoachPastCohort[]>(
      `/api/cohorts/${cohortId}/coaches/${coachUserId}/past-cohorts`,
    );
  },

  /**
   * Submit availability response on behalf of a coach (CM-entered, MED-431)
   */
  submitAvailabilityResponse: async (
    cohortId: string,
    coachUserId: string,
    payload: AdminAvailabilityResponsePayload,
  ): Promise<CoachDetailForModal> => {
    return apiClient.put<CoachDetailForModal>(
      `/api/cohorts/${cohortId}/coaches/${coachUserId}/availability-response`,
      payload,
    );
  },

  // ============================================================================
  // Coach Announcement & Contract Methods (MED-232 / EC-34, EC-35, EC-36)
  // ============================================================================

  /**
   * Get coaches eligible for announcements (those with group assignments)
   */
  getAnnouncementRecipients: async (cohortId: string): Promise<CoachAnnouncementRecipient[]> => {
    return apiClient.get<CoachAnnouncementRecipient[]>(
      `/api/cohorts/${cohortId}/coaches/announcement-recipients`,
    );
  },

  /**
   * Send assignment announcements to coaches
   */
  sendAnnouncements: async (
    cohortId: string,
    coachUserIds: string[],
    emailSubject?: string,
    emailBody?: string,
  ): Promise<SendAnnouncementsResponse> => {
    return apiClient.post<SendAnnouncementsResponse>(
      `/api/cohorts/${cohortId}/coaches/announcements`,
      {
        coach_user_ids: coachUserIds,
        email_subject: emailSubject,
        email_body: emailBody,
      },
    );
  },

  /**
   * Preview coach announcement email with resolved merge tags (MED-761)
   */
  previewAnnouncement: async (
    cohortId: string,
    emailSubject: string,
    emailBody: string,
    coachUserId: string,
  ): Promise<CoachAnnouncementPreviewResponse> => {
    return apiClient.post<CoachAnnouncementPreviewResponse>(
      `/api/cohorts/${cohortId}/coaches/announcements/preview`,
      {
        email_subject: emailSubject,
        email_body: emailBody,
        coach_user_id: coachUserId,
      },
    );
  },

  /**
   * Send test coach announcement email to current user (MED-761)
   */
  sendTestAnnouncement: async (
    cohortId: string,
    emailSubject: string,
    emailBody: string,
    coachUserId?: string,
  ): Promise<SendTestAnnouncementResponse> => {
    return apiClient.post<SendTestAnnouncementResponse>(
      `/api/cohorts/${cohortId}/coaches/announcements/test`,
      {
        email_subject: emailSubject,
        email_body: emailBody,
        coach_user_id: coachUserId,
      },
    );
  },

  /**
   * Mark coach contract as signed
   */
  markContractSigned: async (
    cohortId: string,
    coachUserId: string,
  ): Promise<{ success: boolean; message: string }> => {
    return apiClient.post<{ success: boolean; message: string }>(
      `/api/cohorts/${cohortId}/coaches/${coachUserId}/contract-signed`,
    );
  },

  /**
   * Get callout data for Groups view
   */
  getCalloutData: async (cohortId: string): Promise<CoachCalloutData> => {
    return apiClient.get<CoachCalloutData>(`/api/cohorts/${cohortId}/coaches/callout-data`);
  },

  // ============================================================================
  // Coach Schedule Grid (MED-333)
  // ============================================================================

  /**
   * Get schedule grid data for Coaches > Schedule sub-tab
   */
  getScheduleGrid: async (
    cohortId: string,
    params?: { modality?: string; availability_filter?: string; search?: string; show_other_cohorts?: boolean },
  ): Promise<CoachScheduleGridResponse> => {
    return apiClient.get<CoachScheduleGridResponse>(
      `/api/cohorts/${cohortId}/coaches/schedule${buildQueryString(params)}`,
    );
  },

  // ============================================================================
  // Partner Coach CSV Upload (MED-672)
  // ============================================================================

  uploadPreview: async (
    cohortId: string,
    payload: PartnerCoachUploadRequest,
  ): Promise<PartnerCoachUploadPreviewResponse> => {
    return apiClient.post<PartnerCoachUploadPreviewResponse>(
      `/api/cohorts/${cohortId}/coaches/upload/preview`,
      payload,
    );
  },

  uploadExecute: async (
    cohortId: string,
    payload: PartnerCoachUploadRequest,
  ): Promise<PartnerCoachUploadResult> => {
    return apiClient.post<PartnerCoachUploadResult>(
      `/api/cohorts/${cohortId}/coaches/upload`,
      payload,
    );
  },

  getUploadTemplateUrl: (cohortId: string): string =>
    `/api/cohorts/${cohortId}/coaches/upload/template`,
};

// ============================================================================
// Partner Coach CSV Upload types (MED-672)
// ============================================================================

export type PartnerCoachCSVTargetField =
  | 'first_name'
  | 'last_name'
  | 'email'
  // MED-1355: the personal (non-Medley) email. Required for a new coach.
  | 'preferred_email'
  | 'phone'
  | 'city'
  | 'state_region'
  | 'country'
  | 'timezone';

export interface PartnerCoachCSVColumnMapping {
  csvColumn: string;
  targetField: PartnerCoachCSVTargetField | null;
}

export type PartnerCoachRowStatus =
  | 'new_partner_coach'
  | 'existing_partner_coach'
  | 'existing_medley_coach_dual'
  | 'already_potential'
  | 'error';

export interface PartnerCoachCSVRowResult {
  rowIndex: number;
  status: PartnerCoachRowStatus;
  errors: string[];
  notes: string[];
  data: Partial<Record<PartnerCoachCSVTargetField, string>>;
  existingCoachProfileId?: string;
  existingUserId?: string;
}

export interface PartnerCoachUploadRequest {
  mappings: PartnerCoachCSVColumnMapping[];
  csvData: string[][];
}

export interface PartnerCoachUploadPreviewResponse {
  totalRows: number;
  newPartnerCoaches: number;
  existingPartnerCoaches: number;
  existingMedleyCoachesDual: number;
  alreadyPotential: number;
  errorRows: number;
  rows: PartnerCoachCSVRowResult[];
}

export interface PartnerCoachUploadResult {
  success: boolean;
  created: number;
  matched: number;
  skipped: number;
  addedToPotential: number;
  errors: Array<{ row: number; error: string }>;
}

export default cohortCoachesApi;
